// Analytics configuration and utilities for production

declare global {
    interface Window {
        gtag: (...args: any[]) => void
        dataLayer: any[]
    }
}

// Analytics configuration
export const ANALYTICS_CONFIG = {
    GA_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
    VERCEL_ANALYTICS: process.env.NEXT_PUBLIC_VERCEL_ANALYTICS === 'true',
    VERCEL_SPEED_INSIGHTS: process.env.NEXT_PUBLIC_VERCEL_SPEED_INSIGHTS === 'true',
    PERFORMANCE_MONITORING: process.env.NEXT_PUBLIC_PERFORMANCE_MONITORING === 'true',
    ERROR_REPORTING: process.env.NEXT_PUBLIC_ERROR_REPORTING === 'true',
}

// Google Analytics utilities
export const gtag = {
    // Track page views
    pageview: (url: string) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('config', ANALYTICS_CONFIG.GA_MEASUREMENT_ID, {
                page_path: url,
            })
        }
    },

    // Track events
    event: (action: string, parameters: Record<string, any> = {}) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', action, parameters)
        }
    },

    // Track custom events
    trackEvent: (eventName: string, eventData: Record<string, any> = {}) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', eventName, {
                event_category: eventData.category || 'engagement',
                event_label: eventData.label,
                value: eventData.value,
                ...eventData,
            })
        }
    },

    // Track performance metrics
    trackPerformance: (metricName: string, value: number, unit: string = 'ms') => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', 'performance_metric', {
                event_category: 'Performance',
                event_label: metricName,
                value: Math.round(value),
                custom_parameter_unit: unit,
            })
        }
    },

    // Track user interactions
    trackInteraction: (element: string, action: string, label?: string) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', action, {
                event_category: 'User Interaction',
                event_label: label || element,
                element_type: element,
            })
        }
    },

    // Track form submissions
    trackFormSubmission: (formName: string, success: boolean = true) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', 'form_submit', {
                event_category: 'Form',
                event_label: formName,
                success: success,
            })
        }
    },

    // Track project views
    trackProjectView: (projectSlug: string, projectTitle: string) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', 'view_item', {
                event_category: 'Project',
                event_label: projectTitle,
                item_id: projectSlug,
                content_type: 'project',
            })
        }
    },

    // Track blog post views
    trackBlogView: (postSlug: string, postTitle: string) => {
        if (typeof window !== 'undefined' && window.gtag && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            window.gtag('event', 'view_item', {
                event_category: 'Blog',
                event_label: postTitle,
                item_id: postSlug,
                content_type: 'blog_post',
            })
        }
    },
}

// Performance monitoring utilities
export const performanceMonitoring = {
    // Track Core Web Vitals
    trackWebVitals: (metric: { name: string; value: number; id: string }) => {
        if (ANALYTICS_CONFIG.PERFORMANCE_MONITORING) {
            gtag.trackPerformance(metric.name, metric.value)

            // Also send to console in development
            if (process.env.NODE_ENV === 'development') {
                console.log(`[Performance] ${metric.name}:`, metric.value)
            }
        }
    },

    // Track custom performance metrics
    trackCustomMetric: (name: string, value: number, unit: string = 'ms') => {
        if (ANALYTICS_CONFIG.PERFORMANCE_MONITORING) {
            gtag.trackPerformance(name, value, unit)
        }
    },

    // Track page load time
    trackPageLoad: () => {
        if (typeof window !== 'undefined' && ANALYTICS_CONFIG.PERFORMANCE_MONITORING) {
            window.addEventListener('load', () => {
                const loadTime = performance.now()
                gtag.trackPerformance('page_load_time', loadTime)
            })
        }
    },
}

// Error tracking utilities
export const errorTracking = {
    // Track JavaScript errors
    trackError: (error: Error, errorInfo?: any) => {
        if (ANALYTICS_CONFIG.ERROR_REPORTING) {
            gtag.event('exception', {
                description: error.message,
                fatal: false,
                error_stack: error.stack,
                error_info: errorInfo ? JSON.stringify(errorInfo) : undefined,
            })
        }
    },

    // Track API errors
    trackApiError: (endpoint: string, status: number, message: string) => {
        if (ANALYTICS_CONFIG.ERROR_REPORTING) {
            gtag.event('api_error', {
                event_category: 'API Error',
                event_label: endpoint,
                status_code: status,
                error_message: message,
            })
        }
    },

    // Track 404 errors
    track404: (path: string) => {
        if (ANALYTICS_CONFIG.ERROR_REPORTING) {
            gtag.event('page_not_found', {
                event_category: '404 Error',
                event_label: path,
                page_path: path,
            })
        }
    },
}

// Initialize analytics
export const initializeAnalytics = () => {
    if (typeof window !== 'undefined') {
        // Initialize performance monitoring
        if (ANALYTICS_CONFIG.PERFORMANCE_MONITORING) {
            performanceMonitoring.trackPageLoad()
        }

        // Initialize error tracking
        if (ANALYTICS_CONFIG.ERROR_REPORTING) {
            window.addEventListener('error', (event) => {
                errorTracking.trackError(event.error)
            })

            window.addEventListener('unhandledrejection', (event) => {
                errorTracking.trackError(new Error(event.reason))
            })
        }
    }
}

// Consent management (for GDPR compliance)
export const consentManager = {
    // Check if analytics consent is given
    hasConsent: (): boolean => {
        if (typeof window === 'undefined') return false
        return localStorage.getItem('analytics_consent') === 'true'
    },

    // Grant analytics consent
    grantConsent: () => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('analytics_consent', 'true')
            initializeAnalytics()
        }
    },

    // Revoke analytics consent
    revokeConsent: () => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('analytics_consent', 'false')
            // Disable Google Analytics
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    analytics_storage: 'denied',
                })
            }
        }
    },

    // Initialize consent management
    initConsent: () => {
        if (typeof window !== 'undefined' && ANALYTICS_CONFIG.GA_MEASUREMENT_ID) {
            // Set default consent state
            if (window.gtag) {
                window.gtag('consent', 'default', {
                    analytics_storage: consentManager.hasConsent() ? 'granted' : 'denied',
                })
            }
        }
    },
}

export default {
    gtag,
    performanceMonitoring,
    errorTracking,
    initializeAnalytics,
    consentManager,
    ANALYTICS_CONFIG,
}